import { Calculator, Columns3, Download, History, SlidersHorizontal, Send, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { RecycleAgents } from "@/components/recycle/RecycleAgents";
import { RecycleFilters } from "@/components/recycle/RecycleFilters";
import { RecycleJobs } from "@/components/recycle/RecycleJobs";
import { RecycleSend } from "@/components/recycle/RecycleSend";
import { RecycleTemplates } from "@/components/recycle/RecycleTemplates";
import { RecycleDiagnostic } from "@/components/recycle/RecycleDiagnostic";

const EMPTY = { date_from: null, date_to: null, departments: [], departmentsText: "", regions: [], types: [], energies: [], rooms_min: null, rooms_max: null, size_min: null, size_max: null, dedup: true, exclude_recycled: true, exclude_converted: false, exclude_disabled: false, order: "recent", max_rows: null };
const errText = (e) => e?.response?.data?.detail || "Erreur, réessayez.";
const clean = ({ departmentsText, ...f }) => f;

const Section = ({ icon: Icon, eyebrow, title, children, testid }) => (
  <section className="settings-section" data-testid={testid}>
    <div className="section-title"><Icon size={20} /><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div></div>
    <div className="rc-body">{children}</div>
  </section>
);

export default function RecyclePage() {
  const [facets, setFacets] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [cols, setCols] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [markFile, setMarkFile] = useState(true);
  const [meta, setMeta] = useState({ agents: [], tags: [] });
  const [cfg, setCfg] = useState({ title: "Recyclage", description: "", tags: [], agents: [] });
  const [jobs, setJobs] = useState([]);
  const [loadError, setLoadError] = useState("");

  const loadJobs = useCallback(() => api.get("/recycle/jobs").then(({ data }) => setJobs(data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/recycle/facets").then(({ data }) => { setFacets(data); setCols(data.columns.filter((c) => c.default).map((c) => c.id)); }).catch((e) => setLoadError(errText(e)));
    api.get("/agent-stats/filters").then(({ data }) => setMeta({ agents: data.agents.filter((a) => a.active), tags: data.tags })).catch(() => {});
    loadJobs();
  }, [loadJobs]);
  const seq = useRef(0);
  useEffect(() => {
    if (!facets) return undefined;
    const t = setTimeout(() => compute(f), 700);
    return () => clearTimeout(t);
  }, [f, facets]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!jobs.some((j) => j.status === "running")) return undefined;
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);

  const compute = async (filters = f) => {
    const id = ++seq.current;
    setBusy("preview");
    try {
      const { data } = await api.post("/recycle/preview", clean(filters));
      if (id === seq.current) setResult(data);
    } catch (e) { if (id === seq.current) toast.error(errText(e)); } finally { if (id === seq.current) setBusy(""); }
  };
  const applyTemplate = (t) => {
    const filters = { ...EMPTY, ...t.filters };
    const known = facets.columns.map((c) => c.id);
    setF(filters);
    setCols(known.filter((id) => t.columns.includes(id)));
    setCfg({ title: "Recyclage", description: "", tags: [], agents: [], ...t.send });
  };
  const download = async () => {
    setBusy("export");
    try {
      const res = await api.post("/recycle/export", { filters: clean(f), columns: cols, mark_recycled: markFile, title: cfg.title }, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "recyclage.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      toast.success(`${Number(res.headers["x-row-count"] || 0).toLocaleString("fr-FR")} lignes exportées`);
    } catch (e) { toast.error("Export impossible (aucune ligne ou erreur)."); } finally { setBusy(""); }
    if (markFile) compute();
  };
  const send = async () => {
    setBusy("send");
    try {
      await api.post("/recycle/send", { filters: clean(f), columns: cols, title: cfg.title, description: cfg.description, tags: cfg.tags, agents: cfg.agents, agent_names: Object.fromEntries(meta.agents.filter((a) => cfg.agents.includes(a.id)).map((a) => [String(a.id), a.name])) });
      toast.success("Envoi lancé vers noCRM");
      compute();
      loadJobs();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  return (
    <section className="page" data-testid="recycle-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">BASE PROSPECTS</p>
          <h1>Recycler la base</h1>
          <p className="page-subtitle">Lecture seule de la base PostgreSQL ({facets ? facets.total.toLocaleString("fr-FR") : "…"} annonces). Filtrez, choisissez les colonnes, puis téléchargez le fichier ou créez directement les listes de prospection noCRM (4 999 lignes max par liste).</p>
        </div>
      </header>
      {loadError ? <RecycleDiagnostic message={loadError} /> : !facets ? <p data-testid="recycle-loading">Connexion à la base prospects…</p> : (
        <>
          <div className="rc-live" data-testid="recycle-live-count">
            <Calculator size={18} />
            {busy === "preview" ? <span className="rc-live-busy">Calcul en cours…</span> : result ? <span><strong data-testid="recycle-live-number">{result.count.toLocaleString("fr-FR")}</strong> contacts correspondent à vos filtres{cfg.agents.length > 0 && result.count > 0 && <> · ≈ {Math.ceil(result.count / cfg.agents.length).toLocaleString("fr-FR")} par commercial ({cfg.agents.length})</>}</span> : <span className="rc-live-busy">—</span>}
          </div>
          <RecycleTemplates current={() => ({ filters: f, columns: cols, send: cfg })} onApply={applyTemplate} />
          <Section icon={SlidersHorizontal} eyebrow="1. FILTRES" title="Quels prospects ?" testid="recycle-filters-section">
            <RecycleFilters facets={facets} f={f} set={setF} />
            <div className="rc-row" style={{ marginTop: 12 }}>
              <button type="button" className="secondary-button" onClick={() => setF(EMPTY)} data-testid="recycle-reset">Réinitialiser</button>
            </div>
          </Section>
          <Section icon={Users} eyebrow="2. RÉPARTITION" title="Entre combien de commerciaux découper ?" testid="recycle-split-section">
            <p className="fileprep-dedup-hint">Cochez les commerciaux : la sélection est découpée en parts égales et chacun reçoit sa propre liste de prospection noCRM, à son nom.</p>
            <RecycleAgents agents={meta.agents} count={result ? result.count : null} cfg={cfg} setCfg={setCfg} />
          </Section>
          <Section icon={Columns3} eyebrow="3. COLONNES" title="Champs à extraire" testid="recycle-columns-section">
            <div className="rc-chips">
              {facets.columns.map((c) => (
                <label key={c.id} className={`rc-chip${cols.includes(c.id) ? " on" : ""}`} data-testid={`recycle-col-${c.id}`}>
                  <input type="checkbox" checked={cols.includes(c.id)} onChange={() => setCols(cols.includes(c.id) ? cols.filter((x) => x !== c.id) : facets.columns.map((x) => x.id).filter((id) => id === c.id || cols.includes(id)))} /> {c.label}
                </label>
              ))}
            </div>
          </Section>
          <Section icon={Calculator} eyebrow="4. RÉSULTAT" title="Combien de lignes ?" testid="recycle-result-section">
            <div className="rc-row">
              <button type="button" className="primary-button" onClick={() => compute()} disabled={busy === "preview"} data-testid="recycle-compute">{busy === "preview" ? "Calcul… (jusqu'à 20 s sur toute la base)" : "Recalculer"}</button>
              {result && (() => {
                const n = cfg.agents.length;
                const per = n ? Math.ceil(result.count / n) : 0;
                const lists = n ? n * Math.max(1, Math.ceil(per / 4999)) : result.lists;
                return (
                  <strong data-testid="recycle-count">
                    {result.count.toLocaleString("fr-FR")} prospects → {n ? `${lists} liste${lists > 1 ? "s" : ""} noCRM (${n} commercia${n > 1 ? "ux" : "l"}, ≈ ${per.toLocaleString("fr-FR")} chacun)` : `${lists} liste${lists > 1 ? "s" : ""} de 4 999 max`}
                    {n > 0 && <small className="rc-sub" data-testid="recycle-download-info"> · téléchargement Excel : {result.lists} partie{result.lists > 1 ? "s" : ""}</small>}
                  </strong>
                );
              })()}
              {result && <small data-testid="recycle-recycled-total">{result.recycled_total.toLocaleString("fr-FR")} numéros déjà recyclés par l'outil</small>}
            </div>
            {result && result.sample.length > 0 && (
              <div className="agent-table-wrap" style={{ marginTop: 12 }}>
                <table className="agent-table" data-testid="recycle-sample">
                  <thead><tr>{facets.columns.filter((c) => cols.includes(c.id)).map((c) => <th key={c.id}>{c.label}</th>)}</tr></thead>
                  <tbody>{result.sample.map((r, i) => <tr key={i}>{facets.columns.filter((c) => cols.includes(c.id)).map((c) => <td key={c.id} className="agent-name-cell">{r[c.id]}</td>)}</tr>)}</tbody>
                </table>
              </div>
            )}
          </Section>
          {result && result.count > 0 && (
            <>
              <Section icon={Download} eyebrow="5A. FICHIER" title="Télécharger en Excel" testid="recycle-file-section">
                <div className="rc-row">
                  <button type="button" className="secondary-button" onClick={download} disabled={busy === "export"} data-testid="recycle-download">{busy === "export" ? "Préparation…" : result.lists > 1 ? `Télécharger (.zip de ${result.lists} fichiers)` : "Télécharger (.xlsx)"}</button>
                  <label className="rc-toggle"><input type="checkbox" checked={markFile} onChange={(e) => setMarkFile(e.target.checked)} data-testid="recycle-mark-file" /> Marquer ces numéros comme recyclés</label>
                </div>
              </Section>
              <Section icon={Send} eyebrow="5B. NOCRM" title="Envoyer en listes de prospection" testid="recycle-send-section">
                <RecycleSend agents={meta.agents} tagOptions={meta.tags} count={result.count} lists={result.lists} sending={busy === "send"} onSend={send} cfg={cfg} setCfg={setCfg} showAgents={false} />
              </Section>
            </>
          )}
          <Section icon={History} eyebrow="HISTORIQUE" title="Envois vers noCRM" testid="recycle-jobs-section">
            <RecycleJobs jobs={jobs} />
          </Section>
        </>
      )}
    </section>
  );
}
